import { FC, memo } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import { mobNameKey } from "@/helpers/mobName"
import { MobAttackArea } from "@/modals/constructor/mobs/MobCardModal/ui/MobAttackArea"
import { MobLootGrid } from "@/modals/constructor/mobs/MobCardModal/ui/MobLootGrid"
import { PaperDoll } from "@/ui/PaperDoll"
import { StatBlock } from "@/ui/StatBlock"
import { VitalBars } from "@/ui/VitalBars"
import styles from "./MobCardView.module.scss"
import { IMobCardViewProps } from "./types"

const MobCardViewComponent: FC<IMobCardViewProps> = ({
  status,
  retry,
  typeCode,
  level,
  vitals,
  imageUrl,
  doll,
  lootStrip,
  showLoot,
  statGroups,
  action,
  onClose
}) => {
  const { t } = useTranslation(["mobs", "inventory"])

  return (
    <div className={styles.MobCardView}>
      <header className={styles.MobCardView_header}>
        <h2 className={styles.MobCardView_title}>
          {t(mobNameKey(typeCode), {
            ns: "mobs",
            defaultValue: typeCode
          })}
        </h2>
        <span className={styles.MobCardView_level}>
          {t("card.level", { ns: "mobs", level })}
        </span>
        <button
          type="button"
          className={styles.MobCardView_close}
          aria-label={t("buttons.close", { ns: "common" })}
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {status === "loading" && (
        <div className={styles.MobCardView_center}>
          <SpinnerCircular
            size={48}
            thickness={120}
            speed={120}
            color="#f0f0f0"
            secondaryColor="#3a3a3a"
          />
        </div>
      )}
      {status === "error" && (
        <div className={styles.MobCardView_center}>
          <span>{t("card.loadError", { ns: "mobs" })}</span>
          <button
            type="button"
            className={styles.MobCardView_retry}
            onClick={retry}
          >
            {t("card.retry", { ns: "mobs" })}
          </button>
        </div>
      )}
      {status === "ready" && (
        <>
          <VitalBars vitals={vitals} />
          {imageUrl ? (
            <img
              className={styles.MobCardView_image}
              src={imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className={styles.MobCardView_image} />
          )}
          <PaperDoll
            layout={doll.layout}
            itemsByPosition={doll.itemsByPosition}
          />
          {showLoot && (
            <section className={styles.MobCardView_section}>
              <h3 className={styles.MobCardView_sectionTitle}>
                {t("card.loot", { ns: "mobs" })}
              </h3>
              <MobLootGrid tiles={lootStrip} />
            </section>
          )}
          <StatBlock
            title={t("card.attributes", { ns: "mobs" })}
            rows={statGroups.attributes}
          />
          <StatBlock
            title={t("card.modifiers", { ns: "mobs" })}
            rows={statGroups.modifiers}
          />
          <StatBlock
            title={t("card.derived", { ns: "mobs" })}
            rows={statGroups.derived}
          />
        </>
      )}
      <MobAttackArea action={action} />
    </div>
  )
}

export const MobCardView = memo(MobCardViewComponent)
